import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { fmtDate, fmtDateTime } from "../lib/format";
import {
  X, Loader2, Download, Upload, Trash2, CheckCircle2, XCircle, AlertTriangle, FileText,
  ArrowUpRight, ArrowDownLeft,
} from "lucide-react";
import MandateComplianceActions from "./MandateComplianceActions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TONE = {
  active:    "bg-emerald-50 text-emerald-800 border-emerald-200",
  expired:   "bg-rose-50 text-rose-700 border-rose-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
  completed: "bg-blue-50 text-blue-700 border-blue-200",
};

export default function MandateDetailDrawer({ mandateId, onClose, onChanged }) {
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // string flag while an action runs
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/mandates/${mandateId}`);
      setM(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load mandate");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mandateId]);
  useEffect(() => {
    api.get("/compliance/overview").then((r) => setOverview(r.data)).catch(() => {});
  }, []);

  const act = async (verb, fn) => {
    setBusy(verb);
    try {
      await fn();
      await load();
      onChanged && onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || `Could not ${verb}`);
    } finally {
      setBusy(null);
    }
  };

  const cancel = () => act("cancel", () => api.post(`/mandates/${mandateId}/cancel`));
  const complete = () => act("complete", () => api.post(`/mandates/${mandateId}/complete`));

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    await act("upload", () => api.post(`/mandates/${mandateId}/document`, fd, { headers: { "Content-Type": "multipart/form-data" } }));
  };
  const deleteDoc = () => act("deleteDoc", () => api.delete(`/mandates/${mandateId}/document`));

  const DirIcon = m?.direction === "outgoing" ? ArrowUpRight : ArrowDownLeft;
  const dirTone = m?.direction === "outgoing" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700";

  // For document download — open in a new tab with auth header is awkward, so use a redirect link via blob fetch
  const downloadDoc = async () => {
    const token = localStorage.getItem("pm_token");
    const r = await fetch(`${API}/mandates/${mandateId}/document`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = m?.document_filename || "mandate";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-stretch justify-end" data-testid="mandate-detail">
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3 sticky top-0 bg-white z-10">
          {m && (
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${dirTone}`}>
              <DirIcon size={16} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{m?.direction || "Mandate"}</div>
            <div className="font-display text-lg font-bold tracking-tight truncate">
              {m?.client?.full_name || "Mandate"} {m && <span className="text-slate-400 font-normal text-sm">· {m.partner_agent_name}</span>}
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="detail-close-btn">
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 p-5 space-y-5">
          {loading ? (
            <div className="text-center text-sm text-slate-500 flex items-center gap-2 justify-center"><Loader2 size={12} className="animate-spin" /> Loading…</div>
          ) : !m ? (
            <div className="text-center text-sm text-slate-500">Mandate not available.</div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`pm-pill ${STATUS_TONE[m.status] || ""} capitalize`} data-testid="detail-status">{m.status}</span>
                {m.scope_type && <span className="pm-pill pm-pill-slate capitalize">{m.scope_type}</span>}
                {m.expiry_date && <span className="pm-pill pm-pill-amber">Expires {fmtDate(m.expiry_date)}</span>}
              </div>

              <Section title="Partner">
                <KV k="Agent"   v={m.partner_agent_name} />
                <KV k="Agency"  v={m.partner_agency_name} />
                <KV k="Email"   v={m.partner_agent_email} />
                <KV k="Phone"   v={m.partner_agent_phone} />
              </Section>

              <Section title="Scope">
                <KV k="Type"        v={m.scope_type} />
                <KV k="Clubs"       v={m.scope_clubs?.join(", ")} />
                <KV k="Leagues"     v={m.scope_leagues?.join(", ")} />
                <KV k="Description" v={m.scope_description} />
              </Section>

              <Section title="Validity & commission">
                <KV k="Start"        v={fmtDate(m.start_date)} />
                <KV k="Expiry"       v={fmtDate(m.expiry_date)} />
                <KV k="Reminder"     v={`${m.reminder_days_before} days before expiry`} />
                <KV k="Commission"   v={m.commission_split_pct != null ? `${m.commission_split_pct}%` : "—"} />
              </Section>

              <Section title="Mandate document">
                {m.document_filename ? (
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200">
                    <FileText size={18} className="text-slate-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate">{m.document_filename}</div>
                      <div className="text-[11px] text-slate-500">
                        {(m.document_size / 1024).toFixed(0)} KB · uploaded {fmtDateTime(m.document_uploaded_at)}
                      </div>
                    </div>
                    <button onClick={downloadDoc} className="pm-btn-secondary h-8 px-2.5 rounded-lg text-[11px] font-bold flex items-center gap-1" data-testid="detail-download-btn">
                      <Download size={11} /> Download
                    </button>
                    <button onClick={deleteDoc} disabled={busy} className="pm-btn-secondary h-8 px-2.5 rounded-lg text-[11px] font-bold flex items-center gap-1 hover:bg-rose-50 hover:text-rose-700" data-testid="detail-delete-doc-btn">
                      <Trash2 size={11} />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="text-[12px] text-amber-700 flex items-start gap-1.5 mb-2">
                      <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                      No mandate document uploaded — we recommend storing a signed copy for compliance.
                    </div>
                    <label className="block w-full p-3 rounded-xl border-2 border-dashed border-slate-300 hover:border-emerald-400 transition-colors cursor-pointer text-center" data-testid="detail-upload-dropzone">
                      <input type="file" accept=".pdf,application/pdf,image/png,image/jpeg,image/jpg" onChange={(e) => upload(e.target.files?.[0])} className="hidden" />
                      <div className="text-sm text-slate-500 flex items-center gap-2 justify-center">
                        {busy === "upload" ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                        {busy === "upload" ? "Uploading…" : "Upload signed PDF or image (max 8MB)"}
                      </div>
                    </label>
                  </>
                )}
              </Section>

              {m.notes && (
                <Section title="Internal notes">
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{m.notes}</div>
                </Section>
              )}

              <Section title="Compliance">
                <MandateComplianceActions
                  mandate={m}
                  reload={load}
                  faRegistry={overview?.fa_registry}
                  resendConfigured={overview?.resend_configured}
                />
              </Section>

              <div className="text-[11px] text-slate-400">
                Created by {m.created_by_name} on {fmtDateTime(m.created_at)} · Updated {fmtDateTime(m.updated_at)}
              </div>

              {error && <div className="text-[12px] text-rose-700 flex items-center gap-1.5"><AlertTriangle size={11} /> {error}</div>}
            </>
          )}
        </div>

        {m && (m.status === "active" || m.status === "expired") && (
          <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
            {m.status === "active" && (
              <button onClick={complete} disabled={!!busy} className="pm-btn-secondary h-9 px-3 rounded-lg text-[12px] font-bold flex items-center gap-1.5" data-testid="detail-complete-btn">
                <CheckCircle2 size={11} /> Mark completed
              </button>
            )}
            {m.status === "active" && (
              <button onClick={cancel} disabled={!!busy} className="pm-btn-secondary h-9 px-3 rounded-lg text-[12px] font-bold flex items-center gap-1.5 hover:bg-rose-50 hover:text-rose-700" data-testid="detail-cancel-btn">
                <XCircle size={11} /> Cancel mandate
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function KV({ k, v }) {
  if (!v) return null;
  return (
    <div className="flex text-[13px]">
      <div className="w-24 text-slate-500">{k}</div>
      <div className="flex-1 font-bold text-slate-800 whitespace-pre-wrap break-words">{v}</div>
    </div>
  );
}
